'use client';
import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

interface NewsPost {
  id: string;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  thumbnail_url: string;
  published_at: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  BREAKING_NEWS: 'bg-primary',
  LATEST_NEWS: 'bg-blue-500',
  DEFI: 'bg-blue-600',
  REGULATION: 'bg-crypto-amber',
  MARKETS: 'bg-crypto-green',
  WEB3: 'bg-purple-600'
};

const CATEGORY_LABELS: Record<string, string> = {
  BREAKING_NEWS: 'Breaking News',
  LATEST_NEWS: 'Latest News',
  DEFI: 'DeFi',
  REGULATION: 'Regulation',
  MARKETS: 'Markets',
  WEB3: 'Web3'
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) > 1 ? 's' : ''} ago`;
}

const FALLBACK_ARTICLES = [
{ id: 'news-001', slug: 'bitcoin-hits-new-all-time-high-above-67000', category: 'BREAKING_NEWS', title: 'Bitcoin Hits New All-Time High Above $67,000', excerpt: 'Institutional demand and ETF inflows drive Bitcoin to a new milestone.', thumbnail_url: "https://img.rocket.new/generatedImages/rocket_gen_img_140043a23-1784173923388.png", published_at: new Date(Date.now() - 5 * 60000).toISOString() },
{ id: 'news-002', slug: 'ethereum-upgrade-dencun-successfully-deployed', category: 'MARKETS', title: "Ethereum Upgrade \'Dencun\' Successfully Deployed", excerpt: 'The Dencun upgrade is live on mainnet, improving scalability and lowering fees.', thumbnail_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1054b0551-1766546373811.png", published_at: new Date(Date.now() - 15 * 60000).toISOString() },
{ id: 'news-003', slug: 'defi-tvl-reaches-120b-highest-since-2022', category: 'DEFI', title: 'DeFi TVL Reaches $120B, Highest Since 2022', excerpt: 'Total value locked in DeFi protocols continues to climb sharply.', thumbnail_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1feffb0c8-1764837265072.png', published_at: new Date(Date.now() - 30 * 60000).toISOString() },
{ id: 'news-004', slug: 'sec-approves-spot-ethereum-etf-applications', category: 'REGULATION', title: 'SEC Approves Spot Ethereum ETF Applications', excerpt: 'Major win for the crypto industry as SEC gives the green light.', thumbnail_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15ba1af60-1767805472686.png", published_at: new Date(Date.now() - 60 * 60000).toISOString() }];


export default function NewsGrid() {
  const [articles, setArticles] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    const fetchNews = async () => {
      const { data } = await supabase.
      from('blog_posts').
      select('id, slug, category, title, excerpt, thumbnail_url, published_at').
      eq('is_published', true).
      order('published_at', { ascending: false }).
      limit(4);
      if (data && data.length > 0) setArticles(data);
      setLoading(false);
    };

    fetchNews();

    const channel = supabase.
    channel('news_grid').
    on('postgres_changes', { event: '*', schema: 'public', table: 'blog_posts' }, fetchNews).
    subscribe();

    return () => {supabase.removeChannel(channel);};
  }, []);

  const displayArticles = articles.length > 0 ? articles : FALLBACK_ARTICLES;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Latest News &amp; Insights</h2>
        <Link
          href="/blog"
          className="flex items-center gap-1 text-xs text-crypto-green hover:text-crypto-green/80 font-medium transition-colors duration-150">
          
          View All News <ArrowRight size={13} />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {displayArticles?.map((article) =>
        <Link key={article?.id} href={`/blog/${article?.slug}`} className="block group">
            <article
            id={article?.id}
            className="bg-card border border-border rounded-lg overflow-hidden card-hover h-full flex flex-col transition-all duration-300">
            
              <div className="relative h-40 overflow-hidden bg-muted">
                <AppImage
                src={article?.thumbnail_url || '/assets/images/no_image.png'}
                alt={article?.title}
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
                sizes="(max-width: 640px) 100vw, 50vw" />
              
                <div className="absolute top-2 left-2">
                  <span className={`${CATEGORY_COLORS[article?.category] || 'bg-primary'} text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider`}>
                    {CATEGORY_LABELS[article?.category] || article?.category}
                  </span>
                </div>
                <div className="absolute top-2 right-2">
                  <span className="bg-black/70 text-gray-300 text-[10px] px-2 py-0.5 rounded">
                    {timeAgo(article?.published_at)}
                  </span>
                </div>
              </div>
              <div className="p-3 flex flex-col gap-2 flex-1">
                <h3 className="text-sm font-semibold text-foreground leading-snug group-hover:text-crypto-green transition-colors duration-150 line-clamp-2">
                  {article?.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                  {article?.excerpt}
                </p>
                <div className="mt-auto pt-1">
                  <span className="flex items-center gap-1 text-xs text-primary font-medium group-hover:gap-2 transition-all duration-150">
                    Read More <ArrowRight size={11} />
                  </span>
                </div>
              </div>
            </article>
          </Link>
        )}
      </div>
    </div>);

}